<template>
  <custom-dropdown ref="dropdown"
                   :show-timeout="100"
                   trigger="click"
                   @visible-change="handleVisibleChange"
                   @keydown.native.down.stop.prevent="navigateOptions('next')"
                   @keydown.native.up.stop.prevent="navigateOptions('prev')"
                   @keydown.native.enter.prevent="selectOption"
                   @keydown.native.esc.stop.prevent="$refs.dropdown.visible = false">
    <slot></slot>
    <el-dropdown-menu ref="dropdownMenu" slot="dropdown" class="no-p----adding">
      <div class="dropdown-top">
        <slot name="dropdown"></slot>
        <el-input
          ref="reference"
          v-model="selectedLabel"
          type="text"
          :placeholder="currentPlaceholder"
          :name="name"
          :id="id"
          :autocomplete="autocomplete"
          :size="selectSize"
          :validate-event="false"
          :class="{ 'is-focus': true }"
          @keyup.native="debouncedOnInputChange"
          @keydown.native.down.stop.prevent="navigateOptions('next')"
          @keydown.native.up.stop.prevent="navigateOptions('prev')"
          @keydown.native.enter.prevent="selectOption"
          @keydown.native.esc.stop.prevent="$refs.dropdown.visible = false"
          @keydown.native.tab="$refs.dropdown.visible = false"
          @paste.native="debouncedOnInputChange"
          @mouseenter.native="inputHovering = true"
          @mouseleave.native="inputHovering = false">
          <template slot="prefix" v-if="$slots.prefix">
            <slot name="prefix"></slot>
          </template>
          <template slot="suffix">
            <i class="el-select__caret el-input__icon el-icon-circle-close" @click="handleClearClick"></i>
          </template>
        </el-input>
      </div>
      <el-scrollbar
        tag="ul"
        wrap-class="el-select-dropdown__wrap"
        view-class="el-select-dropdown__list"
        ref="scrollbar">
        <slot name="options"></slot>
        <!--<template v-for="option in options">
          <slot :option="option" name="option"></slot>
        </template>-->
      </el-scrollbar>

    </el-dropdown-menu>
  </custom-dropdown>
</template>

<script>
import debounce from 'lodash/debounce'
import { getValueByPath, valueEquals, isIE, isEdge } from 'element-ui/lib/utils/util';
import scrollIntoView from 'element-ui/lib/utils/scroll-into-view';
import Emitter from 'element-ui/lib/mixins/emitter';
import CustomDropdown from './dropdown'
export default {
  mixins: [Emitter],

  componentName: 'ElSelect',

  components: {CustomDropdown},

  props: {
    name: String,
    id: String,
    value: {
      required: true
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    size: String,
    filterable: Boolean,
  },
  data() {
    return {
      options: [],
      cachedOptions: [],
      createdLabel: null,
      createdSelected: false,
      selected: this.multiple ? [] : {},
      inputLength: 20,
      inputWidth: 0,
      initialInputHeight: 0,
      cachedPlaceHolder: '',
      optionsCount: 0,
      filteredOptionsCount: 0,
      visible: false,
      softFocus: false,
      selectedLabel: '',
      hoverIndex: -1,
      query: '',
      previousQuery: null,
      inputHovering: false,
      currentPlaceholder: '',
      menuVisibleOnFocus: false,
      isOnComposition: false,
      isSilentBlur: false,
      hoverOption: -1
    }
  },
  provide() {
    return {
      'select': this,
      'dropdown': this
    };
  },
  computed: {
    debounce() {
      return this.remote ? 300 : 0;
    },
    selectSize() {
      return this.size || this._elFormItemSize || (this.$ELEMENT || {}).size;
    },
    optionsAllDisabled() {
      return this.options.filter(option => option.visible).every(option => option.disabled);
    }
  },
  watch: {
    hoverIndex(val) {
      if (typeof val === 'number' && val > -1) {
        this.hoverOption = this.options[val] || {};
      }
      this.options.forEach(option => {
        option.hover = this.hoverOption === option;
      });
    }
  },
  methods: {
    handleQueryChange(val) {
      if (this.previousQuery === val || this.isOnComposition) return;
      if (
        this.previousQuery === null &&
        (typeof this.filterMethod === 'function' || typeof this.remoteMethod === 'function')
      ) {
        this.previousQuery = val;
        return;
      }
      this.previousQuery = val;
      // this.$nextTick(() => {
      //   if (this.$refs.dropdown.visible) this.broadcast('ElDropdown', 'updatePopper');
      // });
      this.hoverIndex = -1;
      if (this.multiple && this.filterable) {
        this.$nextTick(() => {
          const length = this.$refs.input.value.length * 15 + 20;
          this.inputLength = this.collapseTags ? Math.min(50, length) : length;
          this.managePlaceholder();
          this.resetInputHeight();
        });
      }
      if (this.remote && typeof this.remoteMethod === 'function') {
        this.hoverIndex = -1;
        this.remoteMethod(val);
      } else if (typeof this.filterMethod === 'function') {
        this.filterMethod(val);
        this.broadcast('ElOptionGroup', 'queryChange');
      } else {
        this.filteredOptionsCount = this.optionsCount;
        this.broadcast('ElOption', 'queryChange', val);
        this.broadcast('ElOptionGroup', 'queryChange');
      }
      if (this.defaultFirstOption && (this.filterable || this.remote) && this.filteredOptionsCount) {
        this.checkDefaultFirstOption();
      }
    },

    onInputChange() {
      // console.log('onInputChange')
      if (this.filterable && this.query !== this.selectedLabel) {
        this.query = this.selectedLabel;
        this.handleQueryChange(this.query);
      }
    },

    onOptionDestroy(index) {
      if (index > -1) {
        this.optionsCount--;
        this.filteredOptionsCount--;
        this.options.splice(index, 1);
      }
    },
    scrollToOption(option) {
      // console.log('scrollToOption: %o', option)
      const target = Array.isArray(option) && option[0] ? option[0].$el : option.$el;
      if (this.$refs.scrollbar && target) {
        const menu = this.$refs.scrollbar.$el.querySelector('.el-select-dropdown__wrap');
        scrollIntoView(menu, target);
      }
      this.$refs.scrollbar && this.$refs.scrollbar.handleScroll();
    },
    emitChange(val) {
      if (!valueEquals(this.value, val)) {
        this.$emit('change', val);
      }
    },
    getOption(value) {
      let option;
      const isObject = Object.prototype.toString.call(value).toLowerCase() === '[object object]';
      const isNull = Object.prototype.toString.call(value).toLowerCase() === '[object null]';
      const isUndefined = Object.prototype.toString.call(value).toLowerCase() === '[object undefined]';

      for (let i = this.cachedOptions.length - 1; i >= 0; i--) {
        const cachedOption = this.cachedOptions[i];
        const isEqual = isObject
          ? getValueByPath(cachedOption.value, this.valueKey) === getValueByPath(value, this.valueKey)
          : cachedOption.value === value;
        if (isEqual) {
          option = cachedOption;
          break;
        }
      }
      if (option) return option;
      const label = (!isObject && !isNull && !isUndefined)
        ? value : '';
      let newOption = {
        value: value,
        currentLabel: label
      };
      if (this.multiple) {
        newOption.hitState = false;
      }
      return newOption;
    },
    setSelected() {
      if (!this.multiple) {
        let option = this.getOption(this.value);
        if (option.created) {
          this.createdLabel = option.currentLabel;
          this.createdSelected = true;
        } else {
          this.createdSelected = false;
        }
        this.selectedLabel = option.currentLabel;
        this.selected = option;
        if (this.filterable) this.query = this.selectedLabel;
        return;
      }
      let result = [];
      if (Array.isArray(this.value)) {
        this.value.forEach(value => {
          result.push(this.getOption(value));
        });
      }
      this.selected = result;
      // this.$nextTick(() => {
      //   this.resetInputHeight();
      // });
    },
    selectOption() {
      if (this.options[this.hoverIndex]) {
        this.handleOptionSelect(this.options[this.hoverIndex]);
      }
    },

    deleteSelected(event) {
      event.stopPropagation();
      const value = this.multiple ? [] : '';
      this.$emit('input', value);
      this.emitChange(value);
      // this.$refs.dropdown.visible = false;
      this.$emit('clear');
    },
    handleOptionSelect(option, byClick) {
      if (this.multiple) {
        const value = (this.value || []).slice();
        const optionIndex = this.getValueIndex(value, option.value);
        if (optionIndex > -1) {
          value.splice(optionIndex, 1);
        } else if (this.multipleLimit <= 0 || value.length < this.multipleLimit) {
          value.push(option.value);
        }
        this.$emit('input', value);
        this.emitChange(value);
        if (option.created) {
          this.query = '';
          this.handleQueryChange('');
          this.inputLength = 20;
        }
        if (this.filterable) this.$refs.input.focus();
      } else {
        this.$emit('input', option.value);
        this.emitChange(option.value);
        this.$refs.dropdown.hide()
        // visible = false;
      }
      this.isSilentBlur = byClick;
      this.setSoftFocus();
      if (this.$refs.dropdown.visible) return;
      this.$nextTick(() => {
        this.scrollToOption(option);
      });
    },
    setSoftFocus() {
      this.softFocus = true;
      const input = this.$refs.input || this.$refs.reference;
      if (input) {
        input.focus();
      }
    },
    handleClearClick(event) {
      this.deleteSelected(event);
    },
    handleVisibleChange(isOpen) {
      // console.warn('handleVisibleChange')
      ;(!isOpen) && this.$refs.dropdownMenu.doDestroy(true)
      this.$emit('visibility-change', isOpen)
    },

    handleMenuEnter() {
      // console.warn('handleMenuEnter')
    },
    doDestroy() {
      // console.warn('doDestroy')
    },
  },
  created() {
    this.cachedPlaceHolder = this.currentPlaceholder = this.placeholder;
    if (this.multiple && !Array.isArray(this.value)) {
      this.$emit('input', []);
    }
    if (!this.multiple && Array.isArray(this.value)) {
      this.$emit('input', '');
    }

    this.debouncedOnInputChange = debounce(() => {
      this.onInputChange();
    }, this.debounce);

    this.debouncedQueryChange = debounce((e) => {
      this.handleQueryChange(e.target.value);
    }, this.debounce);

    this.$on('handleOptionClick', this.handleOptionSelect);
    this.$on('setSelected', this.setSelected);
  },

  mounted() {
    // console.log('select dropdown mounted, options: %o', this.options)

    if (this.multiple && Array.isArray(this.value) && this.value.length > 0) {
      this.currentPlaceholder = '';
    }

    const reference = this.$refs.reference;
    if (reference && reference.$el) {
      const sizeMap = {
        medium: 36,
        small: 32,
        mini: 28
      };
      const input = reference.$el.querySelector('input');
      this.initialInputHeight = input.getBoundingClientRect().height || sizeMap[this.selectSize];
    }
    if (this.remote && this.multiple) {
      this.resetInputHeight();
    }
    this.$nextTick(() => {
      if (reference && reference.$el) {
        this.inputWidth = reference.$el.getBoundingClientRect().width;
      }
    });

    this.setSelected();
  },

  beforeDestroy() {
    // if (this.$el && this.handleResize) removeResizeListener(this.$el, this.handleResize);
  },
  navigateOptions(direction) {
    if (!this.visible) {
      this.visible = true;
      return;
    }
    if (this.options.length === 0 || this.filteredOptionsCount === 0) return;
    if (!this.optionsAllDisabled) {
      if (direction === 'next') {
        this.hoverIndex++;
        if (this.hoverIndex === this.options.length) {
          this.hoverIndex = 0;
        }
      } else if (direction === 'prev') {
        this.hoverIndex--;
        if (this.hoverIndex < 0) {
          this.hoverIndex = this.options.length - 1;
        }
      }
      const option = this.options[this.hoverIndex];
      if (option.disabled === true ||
        option.groupDisabled === true ||
        !option.visible) {
        this.navigateOptions(direction);
      }
      this.$nextTick(() => this.scrollToOption(this.hoverOption));
    }
  }
}
</script>
<style>
  .dropdown-top {
    padding: 0px 5px 5px;
    border-bottom: 1px solid #f1f1f1;
  }
</style>



// WEBPACK FOOTER //
// src/components/common/dropdown/SelectDropdown.vue